import { CustomisationConfig } from '~/components/customisation/customisation-config';
import { Notification } from '~/components/notification/notification';
import {
	FieldModel,
	FieldModelMap,
	FormModel,
	Modifiable,
} from '~/models/form-model';
import {
	InsertUpdateResult,
	isInsertUpdateResult,
} from '~/models/insert-update-result';
import { WarningMessages } from '~/models/warning-messages';
import { ObjectUtils } from './utils';

import numeral from 'numeral';

/**
 * Methods of objects of FormModel interface are overwritten. This is a helper class that defines methods related to
 * the FormModel interface.
 */
export default class FormModelUtils {
	/**
	 * MemberAccountStatusField is the source of truth column used to determine whether
	 * a member is active or exited.
	 *
	 * We define a constant here because there are at least 2 different fields related to
	 * storing active / exited data. We want our code to consistently use this one, at least
	 * for now.
	 */
	public static readonly MemberAccountStatusField = 'MDc_Account_Status';
	public static CUSTOM_FIELD_PREFIX = 'ZZ';
	public static CUSTOMISATION_LEVEL_USER_DEFINED_PREFIX = 'ZY';

	private static readonly MODIFICATION_FIELD_ENDINGS: string[] = [
		'z_Mod_User',
		'd_Mod_Date',
		't_Mod_Time',
		'n_US_ID_mod',
		'k_Mod_Timestamp',
	];

	/**
	 * This holds numeric data type codes present in 3rd char of col names eg IMf_Min_Investment
	 * These codes are for short, int, long, float, date respectively
	 * This is used in acurity-search-api.ts to tell how to connect search strings with "STRING_STARTS_WITH" OR "EQUALS"
	 */
	public static readonly NUMERIC_DATA_TYPE_CHARS = [
		's',
		'i',
		'l',
		'f',
		'd',
		'n',
	];
	public static readonly DATA_TYPE_INDEX = 2;

	public static toggleDeleteField(formModel: FormModel): boolean {
		let isDeletedPost: boolean;

		const field = FormModelUtils.getDeletedField(formModel);

		if (field) {
			if (field.value === 'Y') {
				field.value = 'N';
				isDeletedPost = false;
			} else {
				field.value = 'Y';
				isDeletedPost = true;
			}
		}

		return isDeletedPost;
	}

	/**
	 * isMemberActive returns true if an account is active or
	 * false if an account is exited.
	 *
	 * This method expects you to pass in "MDc_Account_Status".
	 */
	public static isMemberAccountStatusActive(value: string): boolean {
		if (value === undefined) {
			// NOTE: Jae: 2020-01-28
			// We do this check to avoid a case where someone passes in:
			// "record['MDc_Account_Status']" but the record doesn't actually have
			// that field set.
			throw new Error('Invalid argument, cannot be undefined.');
		}

		// NOTE(Albert): 2020-05-19 - WEBUI-3397
		// As a result of WEBUI-3141, we add a description to the rendered value of some
		// columns in a search field so when checking if a member status is active, we
		// only want to match against the first character of this value, i.e. the code
		return value.substr(0, 1) === 'C';
	}

	public static getDeletedField(
		formModel: FormModel,
	): FieldModel | undefined {
		if (formModel) {
			return FormModelUtils.getFirstFieldWith(formModel, (field) => {
				if (field) {
					return FormModelUtils.isDeletedField(field.name);
				} else {
					return undefined;
				}
			});
		}
		return undefined;
	}

	private static getFirstFieldWith(
		formModel: FormModel,
		tester: (FieldModel) => boolean,
	): FieldModel | undefined {
		// NOTE: Jae: 2020-01-13 - WEBUI-2826
		// Check that "formModel.fields" is set to prevent crashes when handling a response
		// from a server that's temporarily down.
		if (formModel && formModel.fields) {
			for (const fieldName of Object.keys(formModel.fields)) {
				const field = formModel.fields[fieldName];
				if (tester(field)) {
					return field;
				}
			}
		}
		return undefined;
	}

	public static isIdFieldName(fieldName: string): boolean {
		return fieldName.endsWith('i_Identity');
	}

	public static isDeletedField(fieldName: string): boolean {
		return fieldName.endsWith('_Deleted');
	}

	/**
	 * Checks if a form model (fields map) is a deleted model from its deleted field value.
	 * @param fieldModelMap field model map, considered as a form model in most cases.
	 * @param deletedFieldName optional deleted field name.
	 * @returns true if deleted field value is 'Y', otherwise (undefined, null, blank, 'N') false.
	 * Throws error if form model does not contain delete field when deletedFieldName is not specified.
	 */
	public static isDeleted<T extends string>(
		fieldModelMap: FieldModelMap<T>,
		deletedFieldName?: Extract<T, string>,
	) {
		if (deletedFieldName !== undefined) {
			return fieldModelMap[deletedFieldName].value === 'Y';
		}

		const deletedFieldModel = FormModelUtils.getDeletedField(
			FormModelUtils.makeFormModelWith(fieldModelMap),
		);
		if (deletedFieldModel === undefined) {
			throw Error('Form model does not contain a Deleted field.');
		}
		return deletedFieldModel.value === 'Y';
	}

	/**
	 * getIdFromIdentityField will return the unique auto-incremented number from the table.
	 */
	public static getIdFromIdentityField(record: {
		[fieldName: string]: any;
	}): number {
		for (const key in record) {
			if (FormModelUtils.isIdFieldName(key)) {
				if (typeof record[key] === 'object') {
					if (typeof record[key].value === 'string') {
						return parseInt(record[key].value, 10);
					}
					return record[key].value;
				}
				return record[key];
			}
		}
		return -1;
	}

	/**
	 * cloneModel makes a deep copy of the FormModel
	 */
	public static cloneModel<
		T extends
			| FormModel
			| FieldModel
			| { [key in string]: FieldModel }
			| FormModel[],
	>(formModel: T): T {
		return JSON.parse(JSON.stringify(formModel));
	}

	/**
	 * isExistingModelReadonly determines whether an existing record is readonly or not.
	 * This function is not designed for use with pre-insert forms.
	 */
	public static isExistingModelReadonly(
		formModel: FormModel | undefined | null,
	): boolean {
		if (!formModel || !formModel.fields) {
			// NOTE(Jae): 2020-03-30
			// This case should never happen, but if it does, it makes sense
			// for the form to be readonly if it has no relevant information
			return true;
		}

		let isModelReadOnly = this.areAllFieldsReadonly(formModel);
		// NOTE(Albert): 2020-11-11
		// Form model may contain one/more lists of child form models. If parent
		// form model has any modifiable fields no need to check child form models
		if (isModelReadOnly) {
			Object.keys(formModel).forEach((modelList) => {
				const formModelList: FormModel[] = formModel[modelList];
				if (
					formModelList &&
					Array.isArray(formModelList) &&
					isModelReadOnly
				) {
					formModelList.forEach((childFormModel) => {
						isModelReadOnly =
							this.areAllFieldsReadonly(childFormModel);
					});
				}
			});
		}

		return isModelReadOnly;
	}

	private static areAllFieldsReadonly(formModel: FormModel): boolean {
		let totalFieldCount = 0;
		let readonlyFieldCount = 0;
		for (const fieldName in formModel.fields) {
			if (!formModel.fields.hasOwnProperty(fieldName)) {
				continue;
			}
			totalFieldCount++;
			const fieldModel = formModel.fields[fieldName];
			if (
				fieldModel.modifiable === Modifiable.FALSE ||
				fieldModel.modifiable === Modifiable.INSERT_ONLY
			) {
				readonlyFieldCount++;
			}
		}
		return totalFieldCount === readonlyFieldCount;
	}

	/**
	 * Makes a deep copy of the FormModel and forces the form fields to be readonly.
	 */
	public static cloneFormModelReadonly<T extends FormModel>(formModel: T): T {
		const formModelReadonly = FormModelUtils.cloneModel(formModel);
		for (const fieldName in formModelReadonly.fields) {
			if (!formModelReadonly.fields.hasOwnProperty(fieldName)) {
				continue;
			}
			const field = formModelReadonly.fields[fieldName];
			field.modifiable = Modifiable.FALSE;
		}
		return formModelReadonly;
	}

	public static formatNullValues(formModel: FormModel): FormModel {
		for (const fieldName of Object.keys(formModel.fields)) {
			const field = formModel.fields[fieldName];
			if (field.value == null && field.name) {
				field.value = '';
			}
		}
		return formModel;
	}

	public static clearInvisibleOrReadonlyFields(formModel: FormModel) {
		return this.clearFields(
			formModel,
			(model, fieldName) =>
				model.fields[fieldName].modifiable === Modifiable.FALSE ||
				model.fields[fieldName].visible ||
				this.fieldInFieldEndings(
					fieldName,
					FormModelUtils.MODIFICATION_FIELD_ENDINGS,
				),
		);
	}

	public static clearInvisibleFields(formModel: FormModel) {
		return this.clearFields(
			formModel,
			(model, fieldName) =>
				model.fields[fieldName].visible ||
				this.fieldInFieldEndings(
					fieldName,
					FormModelUtils.MODIFICATION_FIELD_ENDINGS,
				),
		);
	}

	public static clearInvisibleOptionalFields(formModel: FormModel) {
		return this.clearFields(
			formModel,
			(model, fieldName) =>
				(!model.fields[fieldName].required &&
					model.fields[fieldName].visible) ||
				this.fieldInFieldEndings(
					fieldName,
					FormModelUtils.MODIFICATION_FIELD_ENDINGS,
				),
		);
	}

	public static fieldIsInvisible(field: FieldModel, fieldName: string) {
		return (
			field.visible ||
			this.fieldInFieldEndings(
				fieldName,
				FormModelUtils.MODIFICATION_FIELD_ENDINGS,
			)
		);
	}

	public static formModelFrom(obj: { fields: any }): FormModel {
		const formModel: FormModel = <FormModel>{ fields: {} };

		Object.keys(obj.fields).forEach((fieldName) => {
			formModel.fields[fieldName] = FormModelUtils.fieldModelFrom(
				obj.fields[fieldName],
			);
		});

		return formModel;
	}

	public static fieldModelFrom(obj: any): FieldModel {
		const fieldModel = <FieldModel>{
			name: null,
			visible: false,
			modifiable: null,
			required: null,
			value: null,
			fieldDescription: null,
			inputMask: null,
			codeType: null,
			codeAndDescriptionKey: null,
			length: null,
			modifiedByEvent: null,
			fieldValidationList: null,
			endField: null,
			label: null,
			enabled: false,
			codeList: null,
			dataType: null,
			filter: null,
		};

		const props: string[] = [
			'excludeFromSave',
			'visible',
			'convertTo',
		].concat(Object.keys(fieldModel));

		props.forEach((prop) => {
			if (obj.hasOwnProperty(prop)) {
				fieldModel[prop] = obj[prop];
			}
		});
		return fieldModel;
	}

	private static clearFields(
		formModel: FormModel,
		clearFilter: (formModel: FormModel, fieldName: string) => boolean,
	): FormModel {
		for (const fieldName in formModel.fields) {
			if (clearFilter(formModel, fieldName)) {
				delete formModel.fields[fieldName];
			}
		}
		return formModel;
	}

	private static fieldInFieldEndings(
		fieldName: string,
		endings: string[],
	): boolean {
		return endings.some((ending) => fieldName.endsWith(ending));
	}

	/**
	 * Show warning messages populated from the AcurityTransaction
	 */
	public static showWarningMessagesIfPresent(
		model: InsertUpdateResult | WarningMessages,
	) {
		let warningList: string[] = [];
		if (isInsertUpdateResult(model)) {
			const updateList = model.updateResultList;
			if (updateList && updateList.length > 0) {
				warningList.concat(
					updateList.reduce(
						(acc, updateResult) =>
							updateResult.warningMessages &&
							updateResult.warningMessages.length > 0
								? acc.concat(updateResult.warningMessages)
								: acc,
						[] as string[],
					),
				);
			}
			const insertList = model.insertResultList;
			if (insertList && insertList.length > 0) {
				warningList.concat(
					insertList.reduce(
						(acc, insertResult) =>
							insertResult.warningMessages &&
							insertResult.warningMessages.length > 0
								? acc.concat(insertResult.warningMessages)
								: acc,
						[] as string[],
					),
				);
			}
		} else if (
			model &&
			model.warningMessages &&
			model.warningMessages.length > 0
		) {
			warningList = model.warningMessages;
		}
		if (warningList.length > 0) {
			Notification.warning(warningList.join('<br/>'));
		}
	}

	public static getFieldModel(
		formModel: FormModel,
		fieldName: string,
	): FieldModel | undefined {
		return formModel ? formModel.fields[fieldName] : undefined;
	}

	public static makeFormModelWith<T extends string = string>(
		fields: FieldModelMap,
	): FormModel<T> {
		return { fields };
	}

	public static toggleModifiableAndClearValue(
		formModel: FormModel,
		fieldsToToggle: string[],
		condition: boolean,
	): void {
		fieldsToToggle.forEach((field: string) => {
			const fieldModel: FieldModel = formModel.fields[field];
			fieldModel.modifiable = condition
				? Modifiable.TRUE
				: Modifiable.FALSE;
			if (
				fieldModel.dataType.name === 'Currency' ||
				fieldModel.dataType.name === 'Integer'
			) {
				fieldModel.value = '0';
			} else {
				fieldModel.value = '';
			}
		});
	}

	/**
	 * applyFieldConfigToFormModel is used to transform the formModel before its stored
	 * on a form.
	 */
	public static applyFieldConfigToFormModel(
		formModel: FormModel,
		customisationConfig: CustomisationConfig | false,
		applyDefaultValue: boolean,
	): void {
		if (!formModel || !formModel.fields) {
			throw new Error(
				'ClassicPanel.applyFieldConfigToFormModel: Missing formModel.',
			);
		}
		if (customisationConfig === false) {
			// NOTE(Jae): 2020-03-03
			// Explicitly disable this behaviour for forms that utilize classic-panel but aren't
			// customisable.
			// ie. Receipt Processing Centre (Maintenance dialogs)
			//
			// We do this because the default/general case is that this should be set / provided alongside
			// the formModel. Having a hard-error for missed cases is better than having forms having a hidden
			// defect (ie. "defaultValue" feature not working "randomly")
			return;
		}
		if (!customisationConfig) {
			throw new Error(
				'ClassicPanel.applyFieldConfigToFormModel: Missing customisationConfig.',
			);
		}
		if (customisationConfig.functionConfig === undefined) {
			// NOTE(Jae): 2020-03-02
			// Help catch cases where an any-type is passed in.
			throw new Error(
				'ClassicPanel.applyFieldConfigToFormModel: Invalid customisationConfig argument given. functionConfig should be set or null.',
			);
		}
		if (
			customisationConfig.functionConfig === null ||
			!customisationConfig.functionConfig.fieldConfig
		) {
			return;
		}

		if (applyDefaultValue) {
			// Apply default values from function config
			const fieldConfigList =
				customisationConfig.functionConfig.fieldConfig;
			for (const fieldConfig of fieldConfigList) {
				if (
					fieldConfig.defaultValue === undefined ||
					fieldConfig.defaultValue === null
				) {
					continue;
				}
				const fieldName = fieldConfig.name;
				const fieldModel = formModel.fields[fieldName];
				if (!fieldModel) {
					continue;
				}
				if (
					fieldModel.value === fieldConfig.defaultValue ||
					!this.isFieldEmpty(formModel, fieldModel.name)
				) {
					// If already set to default value, change nothing
					// or if the field has already a value, do nothing
					continue;
				}
				fieldModel.value = fieldConfig.defaultValue;
				fieldModel.forceSave = true;
			}
		}
	}

	public static isFieldEmpty(
		formModel: FormModel,
		fieldName: string,
	): boolean {
		if (formModel.fields.hasOwnProperty(fieldName)) {
			let fieldValue = ObjectUtils.getProperty(
				formModel,
				'fields.' + fieldName + '.value',
			);
			if (fieldValue === null || fieldValue === undefined) {
				return true;
			}
			fieldValue = fieldValue ? fieldValue.trim() : '';
			return fieldValue === '';
		}
		return true;
	}

	public static isFieldChanged(
		fieldName: string,
		cleanModel: FormModel,
		possiblyDirtyFormModel: FormModel,
	): boolean {
		if (!possiblyDirtyFormModel.fields.hasOwnProperty(fieldName)) {
			return false;
		}

		let cleanFieldValue = ObjectUtils.getProperty(
			cleanModel,
			'fields.' + fieldName + '.value',
		);
		let possiblyDirtyFieldValue = ObjectUtils.getProperty(
			possiblyDirtyFormModel,
			'fields.' + fieldName + '.value',
		);
		cleanFieldValue = cleanFieldValue ? cleanFieldValue.trim() : '';
		possiblyDirtyFieldValue = possiblyDirtyFieldValue
			? possiblyDirtyFieldValue.trim()
			: '';

		// if its a double field then compare number types to handle cases like '2.00' != '2.0' != '2'
		// This happens for fields like IMf_Inv_Increment field in 'Investment Manger Details' form
		if (
			cleanFieldValue !== possiblyDirtyFieldValue &&
			/^[A-Z][A-Z]f_/.test(fieldName) &&
			possiblyDirtyFormModel.fields[fieldName].dataType !== null
		) {
			cleanFieldValue = numeral(cleanFieldValue).value();
			possiblyDirtyFieldValue = numeral(possiblyDirtyFieldValue).value();
		}
		return cleanFieldValue !== possiblyDirtyFieldValue;
	}

	public static createModelWithOnlyChangedFields(
		formModel: FormModel,
		cleanModel: FormModel,
	): FormModel {
		const changedModel: FormModel = FormModelUtils.makeFormModelWith({});

		for (const fieldName in formModel.fields) {
			if (
				FormModelUtils.shouldSaveFieldOnUpdate(
					fieldName,
					cleanModel,
					formModel,
				)
			) {
				const newValue = ObjectUtils.getProperty(
					formModel,
					'fields.' + fieldName + '.value',
				);
				// noinspection UnnecessaryLocalVariableJS
				const newFieldModel: FieldModel = {
					name: fieldName,
					value: newValue,
				};

				changedModel.fields[fieldName] = newFieldModel;
			}
		}
		// NOTE: WEBUI-4848
		// if the changed model only contains ZZ field, we don't consider the formModel
		// is changed. It is because in SR-17810, we allow the modifiedByEvent fields always to be
		// sent back to the backend as it maybe consumed by ARCHITECT. However, in case of no
		// fields changed, we don't need to send this ZZ field to the backend.
		if (
			Object.keys(changedModel.fields).every((fieldName) =>
				fieldName.startsWith(FormModelUtils.CUSTOM_FIELD_PREFIX),
			)
		) {
			changedModel.fields = {};
		}

		const formModelList = formModel.formModelList;
		if (formModelList) {
			changedModel.formModelList = [];
			for (const index in formModelList) {
				if (!formModelList.hasOwnProperty(index)) {
					continue;
				}
				const childChangedModel =
					FormModelUtils.createModelWithOnlyChangedFields(
						formModelList[index],
						cleanModel.formModelList[index],
					);
				changedModel.formModelList.push(childChangedModel);
			}
		}

		return changedModel;
	}

	public static createModelWithNonEmptyFields(
		formModel: FormModel,
	): FormModel {
		const changedModel = FormModelUtils.makeFormModelWith({});

		for (const fieldName in formModel.fields) {
			if (FormModelUtils.shouldSaveFieldOnAdd(formModel, fieldName)) {
				const newValue = ObjectUtils.getProperty(
					formModel,
					'fields.' + fieldName + '.value',
				);
				// noinspection UnnecessaryLocalVariableJS
				const newFieldModel: FieldModel = {
					name: fieldName,
					value: newValue,
				};

				changedModel.fields[fieldName] = newFieldModel;
			}
		}

		const formModelList = formModel.formModelList;
		if (formModelList) {
			changedModel.formModelList = [];
			formModelList.forEach((childFormModel) => {
				const childChangedModel =
					FormModelUtils.createModelWithNonEmptyFields(
						childFormModel,
					);
				changedModel.formModelList.push(childChangedModel);
			});
		}

		return changedModel;
	}

	private static shouldSaveFieldOnUpdate(
		fieldName: string,
		cleanModel: FormModel,
		possiblyDirtyModel: FormModel,
	): boolean {
		return FormModelUtils.shouldSaveFieldWithCondition(
			possiblyDirtyModel,
			fieldName,
			// For updates, send only the modifiedByEvent=true fields that are ZZ field because these fields may be
			// required by ARCHITECT
			fieldName.startsWith(FormModelUtils.CUSTOM_FIELD_PREFIX),
			() =>
				FormModelUtils.isFieldChanged(
					fieldName,
					cleanModel,
					possiblyDirtyModel,
				),
		);
	}

	private static shouldSaveFieldOnAdd(
		formModel: FormModel,
		fieldName: string,
	): boolean {
		return FormModelUtils.shouldSaveFieldWithCondition(
			formModel,
			fieldName,
			// For inserts, send all modifiedByEvent=true fields
			true,
			() => !FormModelUtils.isFieldEmpty(formModel, fieldName),
		);
	}

	/**
	 * This decides if the field should be sent to the backend. It
	 * 1. checks if the field model exists
	 * 2. in order, takes account of the values of the excludeFromSave, forceSave, modifiedByEvent and modifiable
	 *    properties of the field model. Earlier properties override later properties.
	 * 3. finally the given final condition is evaluated if necessary.
	 *
	 * Note that if the field is not modifiable, it passes the modifiability check if it is an id or deleted field.
	 */
	private static shouldSaveFieldWithCondition(
		formModel: FormModel,
		fieldName: string,
		saveFieldModifiedByEvent: boolean,
		finalCondition: () => boolean,
	): boolean {
		if (!formModel.fields.hasOwnProperty(fieldName)) {
			return false;
		}
		const fieldModel = formModel.fields[fieldName];
		if (!fieldModel) {
			return false;
		}

		if (fieldModel.excludeFromSave === true) {
			return false;
		}
		if (fieldModel.forceSave === true) {
			return true;
		}
		if (saveFieldModifiedByEvent && fieldModel.modifiedByEvent) {
			return true;
		}

		if (fieldModel.modifiable === Modifiable.FALSE) {
			return false;
		}

		return finalCondition();
	}

	public static isFieldTypeNumeric(name: string) {
		if (name && name.length > this.DATA_TYPE_INDEX + 1) {
			const dataType = name[this.DATA_TYPE_INDEX];
			return this.NUMERIC_DATA_TYPE_CHARS.indexOf(dataType) !== -1;
		}
		return false;
	}
}
